import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";

const loginSchema = z.object({
  username: z.string().trim().min(1, { message: "ユーザー名を入力してください" }),
  password: z.string().min(1, { message: "パスワードを入力してください" }),
  remember: z.boolean().default(true),
});

type LoginForm = z.infer<typeof loginSchema>;

const SAMPLE_EMAIL = "sample@sattou.app";
const SAMPLE_PASSWORD = "password123";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [lockMessage, setLockMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: "", password: "", remember: true },
  });

  const callLoginCheck = async (action: string, loginId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("auth-login-check", {
        body: { action, login_id: loginId },
      });
      if (error) return null;
      return data;
    } catch {
      return null;
    }
  };

  const onSubmit = async (values: LoginForm) => {
    setLoading(true);
    setLockMessage(null);

    // 1. Check lock status
    const checkResult = await callLoginCheck("check", values.username);
    if (checkResult?.locked) {
      setLockMessage(
        `アカウントがロックされています。${checkResult.remaining_minutes}分後に再試行してください。`
      );
      setLoading(false);
      return;
    }

    // 2. Attempt login
    const email = values.username.includes("@")
      ? values.username
      : `${values.username}@sattou.app`;

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: values.password,
    });

    if (error) {
      // 3. Record failure
      const failResult = await callLoginCheck("fail", values.username);

      if (failResult?.locked) {
        setLockMessage("ログイン失敗が上限に達しました。アカウントが30分間ロックされます。");
      } else {
        const remaining = failResult?.attempts != null ? 5 - failResult.attempts : null;
        toast({
          variant: "destructive",
          title: "ログインエラー",
          description:
            error.message === "Invalid login credentials"
              ? `ユーザー名またはパスワードが正しくありません${remaining != null && remaining > 0 ? `（残り${remaining}回）` : ""}`
              : error.message,
        });
      }

      setLoading(false);
      return;
    }

    // 4. Record success
    await callLoginCheck("success", values.username);
    setLoading(false);
    navigate("/", { replace: true });
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    setLockMessage(null);

    const { error } = await supabase.auth.signInWithPassword({
      email: SAMPLE_EMAIL,
      password: SAMPLE_PASSWORD,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "デモログインエラー",
        description: "サンプルアカウントでのログインに失敗しました。",
      });
      setDemoLoading(false);
      return;
    }

    setDemoLoading(false);
    navigate("/", { replace: true });
  };

  return (
    <main className="min-h-screen bg-muted flex items-center justify-center">
      <div className="w-full max-w-md px-4">
        <div className="bg-background rounded-lg shadow-sm border border-border px-8 py-10">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold tracking-wider text-primary">SATTOU.</h1>
          </div>

          {lockMessage && (
            <div className="mb-6 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm text-destructive text-center">
              {lockMessage}
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ユーザー名</FormLabel>
                    <FormControl>
                      <Input {...field} type="text" autoComplete="username" className="h-12" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>パスワード</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" autoComplete="current-password" className="h-12" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="remember"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal cursor-pointer">
                      ログイン状態を保持する
                    </FormLabel>
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full h-12 text-base" disabled={loading}>
                {loading ? "ログイン中..." : "ログイン"}
              </Button>
            </form>
          </Form>

          <div className="mt-4 text-center">
            <Link
              to="/reset-password"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              パスワードをお忘れですか？
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base"
              disabled={demoLoading || loading}
              onClick={handleDemoLogin}
            >
              <Play className="mr-2 h-4 w-4" />
              {demoLoading ? "ログイン中..." : "デモを試す"}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground text-center">
              サンプルデータで機能をお試しいただけます
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
